import { OptimizingIcon } from '@components/_SiteWide/OrderDisplay/OptimizingIcon'
import { OrderProducts } from '@components/_SiteWide/OrderDisplay/OrderProducts'
import { OrderStatusTag } from '@components/_SiteWide/OrderDisplay/OrderStatusTag'
import { OrderStores } from '@components/_SiteWide/OrderDisplay/OrderStores'
import { DispatchStatusTag } from '@components/_SiteWide/StoreDisplay/DispatchStatusTag'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { OrderUpdatedTag } from '@components/OrderDispatch/OrderGrid/OrderUpdatedTag'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { getCarrierDeliveryWindow, sortDestinations } from '@utils/general'
import moment from 'moment'
import React from 'react'

import { OrderFlagIcon, TransportationOrderFlag } from '../Flags/FlagIcon'
import { OrderModalButton } from './OrderModalButton'
import { OrderTerminals } from './OrderTerminals'
import { FormatRunoutTime, formatTimeWindow } from './OrderTimeFormats'

export const getDefaultColumns = (showModal, updateOrder, setOrderHistoryStoreId, setAuditOrderId, lockedOrders) => {
  return [
    modalButton(showModal),
    orderFlags(updateOrder, lockedOrders),
    dispatchStatus(),
    destinations(setOrderHistoryStoreId),
    origins,
    products,
    loadingWindow,
    dispatchWindow,
    runout,
    orderNo,
    updatedTag(setAuditOrderId),
  ]
}
export const modalButton = (showModal, lockedOrders) => {
  return {
    width: 100,
    title: '',
    key: 'show_order_modal',
    render: (value, row) => {
      return (
        <>
          <OrderModalButton showModal={showModal} orderId={row.id} className='pl-0 ml-0' />
          {lockedOrders?.some((o) => o.order_id === row.id) && (
            <OptimizingIcon lockedOrder={lockedOrders?.find((o) => o.order_id === row.id)} />
          )}
        </>
      )
    },
  }
}
export const orderFlags = (updateOrder, lockedOrders) => {
  return {
    width: 80,
    title: '',
    key: 'order_flags',
    render: (value, row) => {
      const lockedOrder = lockedOrders?.find((o) => o.order_id === row.id)
      if (lockedOrder) {
        return (
          <Horizontal verticalCenter>
            <Vertical flex={1}>
              <OptimizingIcon lockedOrder={lockedOrder} />
            </Vertical>
            <Vertical flex={1}> {row.type?.toLowerCase() === 'backhaul' && <TransportationOrderFlag />}</Vertical>
          </Horizontal>
        )
      }
      return <OrderFlagIcon row={row} updateOrder={updateOrder} />
    },
  }
}

export const orderState = {
  width: 120,
  title: 'Status',
  key: 'state',
  render: (value, row) => {
    return <OrderStatusTag status={row.state} />
  },
  sorter: (a, b) => {
    const currentState =
      a.carrier_notify_state === 'sent' || a.carrier_notify_state === 'pending' ? a.carrier_notify_state : a.state
    const nextState =
      b.carrier_notify_state === 'sent' || b.carrier_notify_state === 'pending' ? b.carrier_notify_state : b.state
    return currentState > nextState ? 1 : -1
  },
}

export const destinations = (setOrderHistoryStoreId) => {
  return {
    width: 170,
    title: 'Destinations',
    key: 'stores',
    render: (value, row) => <OrderStores order={row} setOrderHistoryStoreId={setOrderHistoryStoreId} />,
    sorter: (a, b) => {
      return sortDestinations(a, b)
    },
  }
}
export const origins = {
  width: 160,
  title: 'Origins',
  key: 'terminals',
  render: (value, row) => <OrderTerminals order={row} showShortName={false} />,
  sorter: (a, b) => {
    const orderBool = a.loads[0].location_name > b.loads[0].location_name
    return orderBool ? 1 : -1
  },
}
export const products = {
  width: 140,
  title: 'Products',
  key: 'products',
  render: (value, row) => <OrderProducts order={row} />,
}
export const loadingWindow = {
  width: 170,
  title: 'Loading Window',
  key: 'load_window',
  render: (value, row) =>
    formatTimeWindow(row.load_window_start, row.load_window_end, row.type?.toLowerCase() !== 'backhaul'),
  sorter: (a, b) => {
    if (
      (!a.load_window_start || moment(a.load_window_start).isBefore(moment())) &&
      (!b.load_window_start || moment(b.load_window_start).isBefore(moment()))
    ) {
      //  if they both are 'now'
      return (moment(a.load_window_end) || moment()) - (moment(b.load_window_end) || moment())
    }
    return (moment(a.load_window_start) || moment()) - (moment(b.load_window_start) || moment())
  },
  sortDirections: ['ascend', 'descend'],
}
export const dispatchWindow = {
  width: 170,
  title: 'Delivery Window',
  key: 'dispatch_window',
  render: (value, row) => {
    if (!row.carrier) {
      return formatTimeWindow(
        row.dispatch_window_start,
        row.dispatch_window_end,
        row.type?.toLowerCase() !== 'backhaul'
      )
    }
    return getCarrierDeliveryWindow(row)
  },
  sorter: (a, b) => {
    if (
      (!a.dispatch_window_start || moment(a.dispatch_window_start).isBefore(moment())) &&
      (!b.dispatch_window_start || moment(b.dispatch_window_start).isBefore(moment()))
    ) {
      //  if they both are 'now'
      return (moment(a.dispatch_window_end) || moment()) - (moment(b.dispatch_window_end) || moment())
    }
    return (moment(a.dispatch_window_start) || moment()) - (moment(b.dispatch_window_start) || moment())
  },
  sortDirections: ['ascend', 'descend'],
}

// We really need to refactor this grid to be Gravi Grid and use the order management columns
export const runout = {
  width: 80,
  title: 'Runout Time',
  key: 'runout_time',
  render: (value, row) => {
    if ((row.type && row.type?.toLowerCase() === 'backhaul') || row.state === 'complete')
      return <Texto appearance='medium'>N/A</Texto>
    return <FormatRunoutTime order={row} />
  },
  sorter: (a, b) => moment(a.dispatch_window_end) - moment(b.dispatch_window_end),
  sortDirections: ['ascend', 'descend'],
}

export const orderNo = {
  width: 100,
  title: 'Order',
  key: 'number',
  render: (value, row) => <Texto appearance='primary'>{row.number}</Texto>,
  sorter: (a, b) => {
    const orderBool = a.number > b.number
    return orderBool ? 1 : -1
  },
}
export const updatedTag = (setAuditOrderId) => ({
  width: 200,
  title: 'Updated',
  key: 'updated_on',
  render: (value, row) => {
    return (
      <OrderUpdatedTag
        style={{ width: '100%', height: '30px', margin: 0 }}
        order={row}
        setAuditOrderId={setAuditOrderId}
      />
    )
  },
  sorter: (a, b) => {
    const date1 = a.updated_on || a.created_date
    const date2 = b.updated_on || b.created_date
    return date1 < date2 ? 1 : -1
  },
  sortDirections: ['ascend', 'descend'],
})

export const dispatchStatus = () => {
  const { scopes } = useSecurityContext()
  return {
    width: 170,
    title: 'Dispatch Status',
    key: 'assigned_driver',
    render: (value, row) => {
      return (
        <div className='vertical-flex-center'>
          <DispatchStatusTag isCarrierUser={scopes?.is_carrier} stackCarrierWithDriver order={row} />
        </div>
      )
    },
    sorter: (a, b) => moment(a.estimated_end) - moment(b.estimated_end),
  }
}
